import { canManageUsers, USER_ROLE_LABELS } from '@blueprint/domain'
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { AccountsApps } from '../data/index.js'
import { sessionRepository, useSessionStore } from '../state/session-store.js'
import { useUiStore } from '../state/ui-store.js'
import styles from './UserMenu.module.css'

/** Initials from the display name, or the email when nobody has set one. */
function initialsOf(displayName: string, email: string): string {
  const source = displayName.trim() || email
  const parts = source.split(/[\s@._-]+/).filter(Boolean)
  const letters = parts.slice(0, 2).map((part) => part[0] ?? '')
  return letters.join('').toUpperCase() || '?'
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      className={styles.itemIcon}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

const ICONS = {
  profile: (
    <Icon>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </Icon>
  ),
  settings: (
    <Icon>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v2.4M12 18.8v2.4M4.5 7.5l2 1.2M17.5 15.3l2 1.2M4.5 16.5l2-1.2M17.5 8.7l2-1.2" />
    </Icon>
  ),
  password: (
    <Icon>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </Icon>
  ),
  appearance: (
    <Icon>
      <path d="M12 3a9 9 0 1 0 0 18c1.2 0 1.8-.8 1.8-1.7 0-.9-.7-1.4-.7-2.2 0-.8.6-1.4 1.5-1.4h1.6A4.8 4.8 0 0 0 21 10.9C21 6.5 16.9 3 12 3Z" />
      <circle cx="7.8" cy="11.2" r="1" fill="currentColor" stroke="none" />
      <circle cx="11.2" cy="7.8" r="1" fill="currentColor" stroke="none" />
      <circle cx="15.6" cy="8.8" r="1" fill="currentColor" stroke="none" />
    </Icon>
  ),
  signOut: (
    <Icon>
      <path d="M14 5.5h4a1.5 1.5 0 0 1 1.5 1.5v10a1.5 1.5 0 0 1-1.5 1.5h-4" />
      <path d="M10 15.5 13.5 12 10 8.5" />
      <path d="M13.5 12h-9" />
    </Icon>
  ),
}

export function UserMenu() {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const setAppearanceOpen = useUiStore((state) => state.setAppearanceOpen)
  const setProfileOpen = useUiStore((state) => state.setProfileOpen)
  const setAccountSettingsOpen = useUiStore((state) => state.setAccountSettingsOpen)
  const setUsersOpen = useUiStore((state) => state.setUsersOpen)
  const user = useSessionStore((state) => state.user)
  const logOut = useSessionStore((state) => state.logOut)
  const menuId = useId()
  // Billing at Teamcentric Accounts, for a tenant admin of a tenant that belongs to an Accounts customer.
  const [billing, setBilling] = useState(false)
  const isTenantAdmin = user?.role === 'TENANT_ADMIN'
  // Support and the super admin from Teamcentric Accounts can only sign out here.
  const isSupport = user?.role === 'SYSTEM_SUPPORT' || user?.role === 'SYSTEM_ADMIN'

  useEffect(() => {
    if (!isTenantAdmin) return setBilling(false)
    let cancelled = false
    sessionRepository()
      .billingAvailable()
      .then((available) => !cancelled && setBilling(available), () => !cancelled && setBilling(false))
    return () => {
      cancelled = true
    }
  }, [isTenantAdmin])

  const openBilling = () => {
    setOpen(false)
    sessionRepository()
      .billingHandoff()
      .then(
        (url) => window.location.assign(url),
        (caught: unknown) => window.alert(caught instanceof Error ? caught.message : 'Billing could not be opened.'),
      )
  }

  // Close on an outside pointer press or Escape. Escape also returns focus to the trigger,
  // which a plain outside click must not do — focus belongs wherever the user clicked.
  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return
      if (wrapRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const openAppearance = () => {
    setOpen(false)
    setAppearanceOpen(true)
  }

  return (
    <>
    <AppSwitcher />
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        {/* The profile photo from Teamcentric Accounts, when there is one. */}
        <span className={styles.avatar} aria-hidden="true">
          {user?.pictureUrl ? (
            <img className={styles.photo} src={user.pictureUrl} alt="" />
          ) : (
            initialsOf(user?.displayName ?? '', user?.email ?? '')
          )}
        </span>
        <span className={styles.identity}>
          <span className={styles.name}>{user?.displayName || user?.email || 'Signed out'}</span>
          <span className={styles.handle}>{USER_ROLE_LABELS[user?.role ?? 'NORMAL_USER']}</span>
        </span>
        <svg
          className={styles.chevron}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
        <span className="visually-hidden">Account menu</span>
      </button>

      {open ? (
        <div className={styles.menu} id={menuId} role="menu" aria-label="Account">
          <div className={styles.signedIn}>
            <span className={styles.signedInLabel}>Signed in as</span>
            <span className={styles.signedInEmail}>{user?.email ?? ''}</span>
          </div>

          <hr className={styles.separator} />

          {isSupport ? null : (
          <>
          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={() => {
              setOpen(false)
              setProfileOpen(true)
            }}
          >
            {ICONS.profile}
            <span className={styles.itemLabel}>My Profile</span>
          </button>

          {/* Managing people is the tenant admin's job, so only they are offered it. */}
          {user && canManageUsers(user) ? (
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => {
                setOpen(false)
                setUsersOpen(true)
              }}
            >
              {ICONS.settings}
              <span className={styles.itemLabel}>Manage users</span>
            </button>
          ) : null}

          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={() => {
              setOpen(false)
              setAccountSettingsOpen(true)
            }}
          >
            {ICONS.settings}
            <span className={styles.itemLabel}>Account Settings</span>
          </button>

          {billing ? (
            <button type="button" role="menuitem" className={styles.item} onClick={openBilling}>
              {ICONS.settings}
              <span className={styles.itemLabel}>Billing</span>
            </button>
          ) : null}

          <hr className={styles.separator} />

          <button type="button" role="menuitem" className={styles.item} onClick={openAppearance}>
            {ICONS.appearance}
            <span className={styles.itemLabel}>Appearance…</span>
          </button>

          <hr className={styles.separator} />
          </>
          )}

          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={() => {
              setOpen(false)
              void logOut()
            }}
          >
            {ICONS.signOut}
            <span className={styles.itemLabel}>Sign Out</span>
          </button>
        </div>
      ) : null}
    </div>
    </>
  )
}

/**
 * The teamcentric app switcher (docs/accounts-integration-dropin.md section 8): the person's
 * products at each company, their Accounts page and, for an account owner, Billing. Shown to
 * everyone signed in while Teamcentric Accounts is on; without it the API answers 404 and
 * there is no switcher. The panel is fixed to the viewport so no container cuts it off.
 */
function AppSwitcher() {
  const userId = useSessionStore((state) => state.user?.id)
  const [apps, setApps] = useState<AccountsApps | null>(null)
  const [panel, setPanel] = useState<CSSProperties | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  useEffect(() => {
    if (!userId) return setApps(null)
    let cancelled = false
    sessionRepository()
      .accountsApps()
      .then((answer) => !cancelled && setApps(answer), () => !cancelled && setApps(null))
    return () => {
      cancelled = true
    }
  }, [userId])

  // Closes on Escape, a press outside, or any scrolling.
  useEffect(() => {
    if (!panel) return
    const inside = (target: EventTarget | null) =>
      target instanceof Node && (panelRef.current?.contains(target) || triggerRef.current?.contains(target))
    const onPointerDown = (event: PointerEvent) => {
      if (!inside(event.target)) setPanel(null)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setPanel(null)
      triggerRef.current?.focus()
    }
    const onScroll = () => setPanel(null)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [panel])

  if (!apps) return null

  const toggle = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    setPanel(panel || !rect ? null : { position: 'fixed', top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) })
  }
  const close = () => setPanel(null)

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.switcher}
        aria-label="Your teamcentric applications"
        aria-haspopup="menu"
        aria-expanded={panel !== null}
        aria-controls={panel ? panelId : undefined}
        onClick={toggle}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          {[5, 12, 19].flatMap((y) => [5, 12, 19].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="2" />))}
        </svg>
      </button>
      {panel ? (
        <div ref={panelRef} className={styles.menu} style={panel} id={panelId} role="menu" aria-label="Your teamcentric applications">
          {apps.apps.map((app) => (
            <a key={`${app.productId}-${app.url}`} role="menuitem" className={styles.item} href={app.url} onClick={close}>
              <span className={styles.itemLabel}>
                {app.name} · {app.company}
              </span>
            </a>
          ))}
          {apps.apps.length > 0 ? <hr className={styles.separator} /> : null}
          <a role="menuitem" className={styles.item} href={apps.accountUrl} onClick={close}>
            <span className={styles.itemLabel}>{apps.accountLabel ?? 'Your account'}</span>
          </a>
          {apps.billingUrl ? (
            <a role="menuitem" className={styles.item} href={apps.billingUrl} onClick={close}>
              <span className={styles.itemLabel}>Billing</span>
            </a>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
